import { FC, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { convertObjectFieldsToVariables } from '@libraryHelpers/typeAdapters'
import { ScriptValueDialog, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import { COMMON_SCRIPT_VARIABLES } from '@mobileConstants'
import { MobileListItem } from '@mobileTypes'
import { Code as CodeIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material'

import { useFetchMultipleObjectsByCodeQuery } from '@redux/api/objects.api'

import { GETCommand } from '@types'

interface DataSourceConfigProps {
  getDataCommands: GETCommand[] | null
}

export const DataSourceConfig: FC<DataSourceConfigProps> = ({ getDataCommands }) => {
  const { control, watch, setValue } = useFormContext<MobileListItem>()
  const [showJsEditor, setShowJsEditor] = useState(false)

  const dataSource = watch('dataSource')
  const prefilledVariables = watch('prefilledVariables') || []
  const localVariables = watch('variables') || []
  const bindType = dataSource?.bindType || 'command'

  // Формируем список команд для выбора
  const commandOptions = useMemo(() => {
    if (!getDataCommands?.length) return []

    return getDataCommands.map((cmd, index) => ({
      key: `command${index + 1}`,
      name: cmd.name || `command${index + 1}`,
      objectCode: cmd.objectCode,
    }))
  }, [getDataCommands])

  const handleBindTypeChange = (newBindType: 'command' | 'js' | 'prefilled') => {
    setValue('dataSource', {
      bindType: newBindType,
      commandKey: newBindType === 'command' ? commandOptions[0]?.key : undefined,
      jsCode: newBindType === 'js' ? '' : undefined,
      prefilledVariable: newBindType === 'prefilled' ? prefilledVariables[0]?.internalId : undefined,
    })
  }

  const handleSaveJsCode = (jsCode: string) => {
    setValue('dataSource', {
      bindType: 'js',
      jsCode,
    })
    setShowJsEditor(false)
  }

  const objectCodes = useMemo(
    () => commandOptions.map(cmd => cmd.objectCode).filter(Boolean),
    [commandOptions]
  )

  const { data: objectModels = [] } = useFetchMultipleObjectsByCodeQuery(objectCodes, {
    skip: objectCodes.length === 0,
  })

  const objectFields = useMemo(() => {
    return commandOptions.reduce<
      Array<{
        name: string
        type: ReturnType<typeof convertObjectFieldsToVariables>
        isArray: boolean
        description?: string
      }>
    >((acc, command) => {
      const objectModel = objectModels.find(model => model.code === command.objectCode)

      if (objectModel?.fields) {
        acc.push({
          name: command.key,
          type: convertObjectFieldsToVariables(objectModel.fields),
          isArray: true,
          description: command.objectCode,
        })
      }

      return acc
    }, [])
  }, [commandOptions, objectModels])

  const dataSourceScriptVariables = useMemo(
    () => [
      ...objectFields,
      { name: 'item', type: 'any', description: 'Current item from the data source' },
      { name: 'list', type: 'any', isArray: true, description: 'Array of all items from the data source' },
      ...COMMON_SCRIPT_VARIABLES,
    ],
    [objectFields]
  )

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Typography variant='subtitle2' sx={{ mb: 2 }}>
          Data Source
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ mb: 2, display: 'block' }}>
          Select the main array that will be used to render the list items
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Bind Type Selector */}
          <FormControl fullWidth size='small'>
            <InputLabel>Source Type</InputLabel>
            <Select
              value={bindType}
              label='Source Type'
              onChange={e => handleBindTypeChange(e.target.value as 'command' | 'js' | 'prefilled')}
            >
              <MenuItem value='command'>Command Response</MenuItem>
              <MenuItem value='js'>JavaScript</MenuItem>
              <MenuItem value='prefilled'>Prefilled Variable</MenuItem>
            </Select>
          </FormControl>

          {/* COMMAND MODE */}
          {bindType === 'command' && (
            <Controller
              name='dataSource.commandKey'
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size='small'>
                  <InputLabel>Command</InputLabel>
                  <Select {...field} label='Command' disabled={commandOptions.length === 0}>
                    {commandOptions.length === 0 ? (
                      <MenuItem value=''>No commands configured</MenuItem>
                    ) : (
                      commandOptions.map(cmd => (
                        <MenuItem key={cmd.key} value={cmd.key}>
                          {cmd.objectCode} ({cmd.name})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              )}
            />
          )}

          {/* JS MODE */}
          {bindType === 'js' && (
            <Box>
              <Button
                variant='outlined'
                fullWidth
                startIcon={<CodeIcon />}
                onClick={() => setShowJsEditor(true)}
              >
                Edit Data Source Script
              </Button>
            </Box>
          )}

          {/* PREFILLED MODE */}
          {bindType === 'prefilled' && (
            <Controller
              name='dataSource.prefilledVariable'
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size='small'>
                  <InputLabel>Prefilled Variable</InputLabel>
                  <Select
                    {...field}
                    label='Prefilled Variable'
                    disabled={prefilledVariables.length === 0}
                  >
                    {prefilledVariables.length === 0 ? (
                      <MenuItem value=''>No prefilled variables configured</MenuItem>
                    ) : (
                      prefilledVariables.map(pv => {
                        const localVar = localVariables.find(v => v.internalId === pv.internalId)
                        const isArray = localVar?.variableType === 'multiple'
                        if (!isArray) return null
                        return (
                          <MenuItem key={pv.internalId} value={pv.internalId}>
                            {pv.internalId}
                          </MenuItem>
                        )
                      })
                    )}
                  </Select>
                </FormControl>
              )}
            />
          )}

          {bindType === 'prefilled' && prefilledVariables.length === 0 && (
            <Typography variant='caption' color='warning.main'>
              Configure Prefilled Variables below to use this source type
            </Typography>
          )}
        </Box>
      </Paper>

      {showJsEditor && (
        <ScriptValueDialog
          isShow
          value={dataSource?.jsCode || ''}
          title='Edit Data Source JavaScript'
          hint='Return an array of objects to display in the list'
          onClose={() => setShowJsEditor(false)}
          onSave={handleSaveJsCode}
          language='js'
          variables={dataSourceScriptVariables}
          fieldsInfo={variablesToFieldsInfo(dataSourceScriptVariables)}
          examples={[
            {
              title: 'Use Command Response',
              description: 'Return array from command',
              code: `return command1`,
              category: 'Basic',
            },
            {
              title: 'Filter Array',
              description: 'Filter items by condition',
              code: `return command1.filter(item => item.status === 'active')`,
              category: 'Array',
            },
            {
              title: 'Combine Arrays',
              description: 'Merge multiple command responses',
              code: `return [...command1, ...command2]`,
              category: 'Advanced',
            },
            {
              title: 'Transform Data',
              description: 'Map and transform array items',
              code: `return command1.map(item => ({
  ...item,
  displayName: item.firstName + ' ' + item.lastName
}))`,
              category: 'Advanced',
            },
          ]}
        />
      )}
    </>
  )
}
